/**
 * TypedHandle - A Handle component that displays type-based colors
 *
 * Port types are visually distinguished by color to help
 * users understand what connections are valid.
 */

import { memo } from 'react'
import { Handle, HandleProps, Position } from 'reactflow'
import { getPortTypeColor, getPortTypeLabel, getPortTypeHexColor, PortType } from './portTypes'

interface TypedHandleProps extends Omit<HandleProps, 'style'> {
  /** Port type for color styling */
  portType: PortType | string
  /** Override the default size (in pixels) */
  size?: number
  /** Whether to show a tooltip with type info */
  showTooltip?: boolean
  /** Custom style overrides */
  style?: React.CSSProperties
}

/**
 * Handle sizes by context
 */
export const DEFAULT_SIZE = 12
export const MINI_SIZE = 8

export const TypedHandle = memo(function TypedHandle({
  portType,
  size = DEFAULT_SIZE,
  showTooltip = true,
  style,
  ...handleProps
}: TypedHandleProps) {
  const hexColor = getPortTypeHexColor(portType)
  const typeLabel = getPortTypeLabel(portType)

  // Position-based offset to avoid overlapping node border
  const isLeft = handleProps.position === Position.Left
  const isRight = handleProps.position === Position.Right

  const handleStyle: React.CSSProperties = {
    width: size,
    height: size,
    background: hexColor,
    border: '2px solid rgba(255,255,255,0.3)',
    ...(isLeft && { left: -(size / 2 + 1) }),
    ...(isRight && { right: -(size / 2 + 1) }),
    ...style,
  }

  return (
    <Handle
      {...handleProps}
      style={handleStyle}
      title={showTooltip ? `${handleProps.id} (${typeLabel})` : undefined}
    />
  )
})

/**
 * Inline port indicator for showing port type in node UI.
 * Purely visual, not a React Flow handle.
 */
interface PortIndicatorProps {
  portType: PortType | string
  label?: string
  className?: string
}

export const PortIndicator = memo(function PortIndicator({ portType, label, className = '' }: PortIndicatorProps) {
  const colors = getPortTypeColor(portType)
  const typeLabel = getPortTypeLabel(portType)

  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xxs ${colors.bg} ${colors.text} ${className}`}
      title={typeLabel}
    >
      <span
        className={`w-2 h-2 rounded-full ${colors.border} border`}
        style={{ backgroundColor: getPortTypeHexColor(portType) }}
      />
      {label && <span>{label}</span>}
    </div>
  )
})

export { getPortTypeHexColor }
